<template>
    <div id="style-guide">
        <GuideSummary id="guide-summary" :summary="summary" />

        <div id="guide-page">
            <GuideSpecification id="guide-content" ref="specificationsRoot" title="Guia de Estilos">
                <GuideSpecification title="Tipografia">
                    <GuideSpecification title="Cabeçalhos">
                        <GuideSampleCode :framed="true">
                            <h1>Lorem Ipsum</h1>
                            <h2>Lorem Ipsum</h2>
                            <h3>Lorem Ipsum</h3>
                            <h4>Lorem Ipsum</h4>
                            <h5>Lorem Ipsum</h5>
                            <h6>Lorem Ipsum</h6>
                        </GuideSampleCode>
                    </GuideSpecification>

                    <GuideSpecification title="Estilo">
                        <div class="alert-warning mb">
                            ! Não escreva texto em maiúscula manualmente. Use CSS pra caso mudemos
                            os padrões algum dia :)
                        </div>

                        <GuideSampleCode :framed="true">
                            <p class="text-secondary">Secundário</p>
                            <p class="text-accent">Destacado</p>
                            <p class="text-accent-hover">Destacado no hover</p>
                            <p class="text-underline">Sublinhado</p>
                            <p class="text-uppercase">Maiúsculas</p>
                            <p class="text-lowercase">Minúsculas</p>
                        </GuideSampleCode>
                    </GuideSpecification>
                </GuideSpecification>

                <GuideSpecification title="Cor">
                    <GuideSpecification
                        v-for="(variant, index) in styleVariants"
                        :key="index"
                        :title="variant"
                    >
                        <div
                            class="d-flex flex-spaced-fixed flex-wrap text-center"
                            style="color: white"
                        >
                            <div v-for="n in 5" class="flex-item-grow">
                                <div
                                    :ref="`${variant}ColorEls`"
                                    :class="`bg-${variant}-${n}`"
                                    class="frame"
                                >
                                    <span>.bg-{{ variant }}-{{ n }}</span
                                    ><br />
                                    (<strong
                                        v-if="colors[variant] && colors[variant][n - 1]"
                                        class="text-uppercase"
                                        >{{ colors[variant][n - 1] }}</strong
                                    >)
                                </div>
                                <div :class="`text-${variant}-${n}`" class="mt">
                                    <span>.text-{{ variant }}-{{ n }}</span
                                    ><br />
                                    (<strong
                                        v-if="colors[variant] && colors[variant][n - 1]"
                                        class="text-uppercase"
                                        >{{ colors[variant][n - 1] }}</strong
                                    >)
                                </div>
                            </div>
                        </div>
                    </GuideSpecification>
                </GuideSpecification>

                <GuideSpecification title="Layout">
                    <GuideSpecification title="Alinhamento">
                        <GuideSampleCode :framed="true">
                            <p class="text-left">Esquerda</p>
                            <p class="text-center">Centro</p>
                            <p class="text-right">Direita</p>
                        </GuideSampleCode>
                    </GuideSpecification>

                    <GuideSpecification title="Posicionamento">
                        <ul>
                            <li><code>.pos-absolute</code>: aplica posição absoluta</li>
                            <li><code>.pos-fixed</code>: aplica posição fixa</li>
                            <li>
                                <code>.pos-[top | right | left | bottom]</code>: posiciona o
                                elemento na coordenada 0 da opção (top, left, etc)
                            </li>
                            <li><code>.pos-center</code>: posiciona o elemento no centro</li>
                            <li>
                                <code>.pos-center-x</code>: posiciona o elemento no centro do eixo x
                            </li>
                            <li>
                                <code>.pos-center-y</code>: posiciona o elemento no centro do eixo y
                            </li>
                        </ul>
                    </GuideSpecification>

                    <GuideSpecification title="Espaçamento">
                        <p>Há classes auxiliares pra aplicar espaçamento.</p>
                        <p>
                            Elas são no formato
                            <code>.[tipo][direção]-[tamanho]-[breakpoint]</code>.
                        </p>
                        <ul>
                            <li>
                                <strong>tipo:</strong>
                                <ul>
                                    <li>m: margem</li>
                                    <li>p: padding</li>
                                </ul>
                            </li>
                            <li>
                                <strong>direção:</strong>
                                <ul>
                                    <li>t: top</li>
                                    <li>b: bottom</li>
                                    <li>l: left</li>
                                    <li>r: right</li>
                                    <li>x: left e right</li>
                                    <li>y: top e bottom</li>
                                </ul>
                            </li>
                            <li>
                                <strong>tamanho (opcional)</strong>: de 0 a 5 (correspondem a 0 ,
                                .25rem, .5rem, 1rem, 2rem) ou "auto"; quando omitido aplica-se o
                                valor padrão (1rem)
                            </li>
                            <li><strong>breakpoint (opcional)</strong>: de xs a lg</li>
                        </ul>

                        <p>Exemplos:</p>
                        <ul>
                            <li><code>.mt</code></li>
                            <li><code>.mb-auto</code></li>
                            <li><code>.mb-2</code></li>
                            <li><code>.mx-auto-xs</code></li>
                        </ul>
                    </GuideSpecification>

                    <GuideSpecification title="Display">
                        <p>
                            Classes auxiliares pra propriedade
                            <code>display</code>:
                        </p>
                        <ul>
                            <li><code>.d-none</code></li>
                            <li><code>.d-block</code></li>
                            <li><code>.d-flex</code></li>
                            <li><code>.d-inline</code></li>
                            <li><code>.d-inline-block</code></li>
                            <li><code>.d-inline-flex</code></li>
                        </ul>
                    </GuideSpecification>

                    <GuideSpecification title="Aspect">
                        <p>
                            Classes para criar blocos com proporção altura/largura fixa. Úteis como
                            containers pra fotos, tendo o tamanho certo antes mesmo delas
                            carregarem.
                        </p>
                        <p>
                            As classes seguem o padrão
                            <code>.aspect-{tipo de tela}-{largura x altura}</code>
                        </p>
                        <p>
                            Opções pro <strong>tipo de tela</strong>:
                            <code>xs, sm, md, lg</code>
                        </p>
                        <p>
                            Opções pra <strong>largura x altura</strong>:
                            <code>1-1; 4-3; 4-5; 5-4; 16-9; 48-17; 21-9; 125-34</code>
                        </p>
                        <GuideSampleCode>
                            <div class="aspect-xs-16-9 frame">Bloco 16:9</div>
                        </GuideSampleCode>
                    </GuideSpecification>

                    <GuideSpecification title="Frame">
                        <p>Container para agrupar conteúdo de um mesmo contexto.</p>
                        <GuideSampleCode>
                            <div class="frame">Texto aqui.</div>
                        </GuideSampleCode>

                        <GuideSampleCode>
                            <div
                                class="border border-2 border-solid border-default-2 rounded px py"
                            >
                                Texto aqui.
                            </div>
                        </GuideSampleCode>
                    </GuideSpecification>

                    <GuideSpecification title="d-flex">
                        <p>Classes pro elemento pai:</p>
                        <ul>
                            <li>
                                <code>.d-flex</code> usa flexbox e expande os filhos dentro do
                                container.
                            </li>
                            <li><code>.flex-wrap</code> habilita a quebra de linha.</li>
                            <li>
                                <code>.flex-spaced-fixed</code> habilita espaçamento entre os
                                filhos.
                            </li>
                        </ul>

                        <p>Classes pros elementos filhos:</p>
                        <ul>
                            <li>
                                <code>.flex-child-fixed</code> fixa o elemento filho à sua largura
                                original (não o expande)
                            </li>
                        </ul>

                        <GuideSampleCode>
                            <div class="d-flex flex-wrap flex-spaced-fixed">
                                <div v-for="i in 10" class="frame">
                                    {{ i }}
                                </div>
                            </div>
                        </GuideSampleCode>
                    </GuideSpecification>

                    <GuideSpecification title="Grid">
                        <GuideSpecification title=".flex-grid">
                            <p>Utiliza flexbox.</p>
                            <p>
                                Filhos têm classe com prefixo
                                <code>.flex-</code>
                            </p>
                            <GuideSampleCode>
                                <div class="flex-grid">
                                    <div
                                        v-for="i in 6"
                                        class="flex-xs-12 flex-sm-6 flex-md-4 flex-lg-3"
                                    >
                                        <div class="frame">{{ i }}</div>
                                    </div>
                                </div>
                            </GuideSampleCode>
                        </GuideSpecification>

                        <GuideSpecification title=".row">
                            <p>Utiliza float nos filhos.</p>
                            <p>Filhos têm classe com prefixo <code>.col-</code></p>
                            <p>Não tem espaçamento vertical.</p>
                            <GuideSampleCode>
                                <div class="row">
                                    <div
                                        v-for="i in 6"
                                        class="col-xs-12 col-sm-6 col-md-4 col-lg-3"
                                    >
                                        <div class="frame">{{ i }}</div>
                                    </div>
                                </div>
                            </GuideSampleCode>
                        </GuideSpecification>
                    </GuideSpecification>

                    <GuideSpecification title="Media Queries">
                        <p>
                            Ao criar novos media queries em relação à largura, utilize sempre as
                            variáveis presentes em
                            <code>cr-variables</code>.
                        </p>

                        <ul>
                            <li>
                                <code>@min-screen-xs</code> e
                                <code>@max-screen-xs</code>
                            </li>
                            <li>
                                <code>@min-screen-sm</code> e
                                <code>@max-screen-sm</code>
                            </li>
                            <li>
                                <code>@min-screen-md</code> e
                                <code>@max-screen-md</code>
                            </li>
                            <li>
                                <code>@min-screen-lg</code> e
                                <code>@max-screen-lg</code>
                            </li>
                        </ul>

                        <p>
                            Isso é pra evitar que os elementos fiquem incorretos nos breakpoints da
                            media query. Se for utilizar valores diferentes, crie as regras opostas
                            com uma diferença de 1px, como abaixo:
                        </p>
                        <code>
                            @media (min-width: 100px) { ... } @media (max-width: 99px) {...}
                        </code>
                    </GuideSpecification>

                    <GuideSpecification title="Responsividade">
                        <p>Classes para visiblidade:</p>
                        <ul>
                            <li><code>.hidden-xs-down</code>: abaixo de 576px</li>
                            <li><code>.hidden-xs-up</code>: acima de 576px</li>
                            <li><code>.hidden-sm-down</code>: abaixo de 768px</li>
                            <li><code>.hidden-sm-up</code>: acima de 768px</li>
                            <li><code>.hidden-md-down</code>: abaixo de 996px</li>
                            <li><code>.hidden-md-up</code>: acima de 996px</li>
                            <li><code>.hidden-lg-down</code>: abaixo de 1200px</li>
                            <li><code>.hidden-lg-up</code>: acima de 1200px</li>
                        </ul>
                    </GuideSpecification>

                    <GuideSpecification title="Shadows">
                        <GuideSampleCode>
                            <div class="d-flex flex-spaced-fixed">
                                <div
                                    v-for="n in 5"
                                    :class="`shadow-${n}`"
                                    class="flex-item-grow"
                                    style="background-color: white; height: 40px"
                                ></div>
                            </div>
                        </GuideSampleCode>
                    </GuideSpecification>
                </GuideSpecification>

                <GuideSpecification title="Estilos (CSS)">
                    <GuideSpecification title="Alert">
                        <GuideSampleCode>
                            <div
                                v-for="variant in styleVariants"
                                :class="`alert-${variant}`"
                                class="mb"
                            >
                                .alert-{{ variant }}
                            </div>
                        </GuideSampleCode>
                    </GuideSpecification>

                    <GuideSpecification title="Badge">
                        <GuideSampleCode>
                            <p v-for="variant in styleVariants">
                                <span :class="`badge-${variant}`">badge-{{ variant }}</span>
                            </p>
                        </GuideSampleCode>
                    </GuideSpecification>

                    <GuideSpecification title="Button">
                        <GuideSpecification title="Sizing">
                            <GuideSampleCode>
                                <p><a class="btn btn-sm">Pequeno</a></p>
                                <p><a class="btn">Padrão</a></p>
                                <p><a class="btn btn-lg">Grande</a></p>
                                <p><a class="btn btn-xl">Extra Grande</a></p>
                            </GuideSampleCode>
                        </GuideSpecification>
                        <GuideSpecification title="Button Group">
                            <GuideSampleCode>
                                <div class="form-inline">
                                    <div class="btn-group">
                                        <button class="btn" type="button">AAA</button>
                                        <button class="btn" type="button">AAA</button>
                                    </div>

                                    <div class="btn-group">
                                        <button class="btn" type="button">AAA</button>
                                        <button class="btn" type="button">AAA</button>
                                        <button class="btn" type="button">AAA</button>
                                    </div>
                                </div>
                            </GuideSampleCode>
                        </GuideSpecification>
                        <GuideSpecification title="Variations">
                            <GuideSampleCode>
                                <button class="btn" type="button">default</button>
                                <button class="btn btn-primary" type="button">btn-primary</button>
                                <button class="btn btn-light" type="button">btn-light</button>
                                <button class="btn btn-success" type="button">btn-success</button>
                                <button class="btn btn-warning" type="button">btn-warning</button>
                                <button class="btn btn-danger" type="button">btn-danger</button>
                            </GuideSampleCode>
                        </GuideSpecification>
                        <GuideSpecification title="Image and icons">
                            <GuideSampleCode>
                                <a class="btn btn-image">
                                    <img
                                        :src="`${$scope.CDN_IMAGES}/images/site/2017/icones/email2.svg`"
                                    />
                                    <span>Com imagem</span>
                                </a>
                                <a class="btn btn-image">
                                    <span class="icon-cr icon-close white"></span>
                                    <span>Com imagem</span>
                                </a>
                            </GuideSampleCode>
                        </GuideSpecification>
                    </GuideSpecification>

                    <GuideSpecification title="Form">
                        <GuideSpecification title="Form Group">
                            <GuideSampleCode>
                                <form>
                                    <div class="form-group">
                                        <label>Texto:</label>
                                        <input type="text" />
                                    </div>

                                    <div class="form-group">
                                        <label>Select</label>
                                        <div class="cr-select">
                                            <select>
                                                <option>A</option>
                                                <option>B</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <input id="checkbox" class="cr-checkbox" type="checkbox" />
                                        <label for="checkbox">Checkbox</label>
                                    </div>
                                    <div class="form-group">
                                        <input id="switch" class="cr-switch" type="checkbox" />
                                        <label for="switch">Switch</label>
                                    </div>
                                    <div class="form-group">
                                        <input id="radio" class="cr-radio" type="radio" />
                                        <label for="radio">Radio</label>
                                    </div>
                                </form>
                            </GuideSampleCode>
                        </GuideSpecification>
                        <GuideSpecification title="Input Group">
                            <GuideSampleCode>
                                <form class="form-inline">
                                    <div class="input-group">
                                        <label>Texto:</label>
                                        <input type="text" />
                                    </div>
                                    <div class="input-group">
                                        <input type="number" />
                                        <label>segundos</label>
                                    </div>
                                </form>

                                <div class="form-inline">
                                    <div class="form-group">
                                        <label>Preço:</label>
                                        <div class="input-group">
                                            <label>De:</label>
                                            <input type="text" />
                                            <label>Por:</label>
                                            <input type="number" />
                                            <label>(promo)</label>
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <label>Preço Lojista:</label>
                                        <div class="input-group">
                                            <label>De:</label>
                                            <input type="text" />
                                            <label>Por:</label>
                                            <input type="number" />
                                            <button class="btn">Vender</button>
                                        </div>
                                    </div>
                                </div>
                            </GuideSampleCode>
                        </GuideSpecification>
                        <GuideSpecification title="Form inline">
                            <GuideSampleCode>
                                <form class="form-inline">
                                    <label for="input-inline-text">Texto:</label>
                                    <input id="input-inline-text" required type="text" />

                                    <input id="switch-inline" class="cr-switch" type="checkbox" />
                                    <label for="switch-inline">Switch</label>

                                    <input class="btn" type="button" value="Confirmar" />
                                </form>
                            </GuideSampleCode>
                        </GuideSpecification>
                        <GuideSpecification title="Colors">
                            <GuideSampleCode>
                                <div v-for="variant in styleVariants" class="form-group">
                                    <label>
                                        {{ variant }}
                                        <br /><small class="text-secondary"
                                            >Informação adicional</small
                                        >
                                    </label>
                                    <input :class="`input-${variant}`" type="text" />
                                    <small :class="`text-${variant}`">Feedback adicional</small>
                                </div>
                            </GuideSampleCode>
                        </GuideSpecification>
                    </GuideSpecification>

                    <GuideSpecification title="Icons">
                        <p>
                            <a class="text-accent" href="/hotsite.php?name=icons-cr-list"
                                >Clique aqui</a
                            >
                            para ver a lista de ícones.
                        </p>
                        <p>
                            Os ícones sempre devem ser incluídos em um elemento
                            <code>i</code> com a classe <code>.icon-cr</code>, a classe do ícone
                            <code>.icon-{nome}</code> e a classe da cor (opcional). Se nenhuma
                            classe de cor for usada, será adotada a padrão.
                        </p>
                        <p>Classes de cor:</p>
                        <ul>
                            <li><code>.primary</code></li>
                            <li><code>.default</code></li>
                            <li><code>.light</code></li>
                            <li><code>.info</code></li>
                            <li><code>.success</code></li>
                            <li><code>.danger</code></li>
                        </ul>

                        <p>Classes de cor obsoletas (serão removidas):</p>
                        <ul>
                            <li><code>.support</code></li>
                            <li><code>.accent</code></li>
                            <li><code>.orange</code></li>
                            <li><code>.blue</code></li>
                            <li><code>.grey</code></li>
                        </ul>

                        <p>
                            Se o elemento <code>i</code> for um filho imediato de
                            <code>h1, h2, h3, h4, h5, h6, p, span</code> ele será alinhado ao texto.
                            Em ícones mais largos, pode-se acrescentar um <code>&amp;nbsp;</code>
                            entre o texto e o ícone para funcionar como margem.
                        </p>

                        <p>
                            Pode ser utilizado com a classe
                            <code>.icon-cr-hover</code> pra dar feedback visual.
                        </p>

                        <p>
                            Ao utilizar dentro de um botão, adicione a classe
                            <code>.btn-image</code> ao botão.
                        </p>

                        <GuideSampleCode>
                            <h3><i class="icon-cr icon-heart accent"></i>Coração</h3>
                            <p><i class="icon-cr icon-heart"></i>Coração</p>
                            <p><i class="icon-cr icon-sizes"></i>&nbsp;Régua</p>
                            <a class="btn btn-image"
                                ><i class="icon-cr icon-plus white"></i>Botão</a
                            >
                            <i class="icon-cr icon-cr-hover icon-close"></i>
                            <i class="icon-cr icon-reviews-item"></i>
                        </GuideSampleCode>
                    </GuideSpecification>

                    <GuideSpecification title="Pagination">
                        <p>Com números das páginas</p>
                        <GuideSampleCode>
                            <nav>
                                <ul class="cr-pagination">
                                    <li class="cr-pagination-previous">
                                        <a aria-label="Anterior">Anterior</a>
                                    </li>
                                    <li>
                                        <a>1</a>
                                    </li>
                                    <li class="cr-pagination-skip"></li>
                                    <li>
                                        <a>4</a>
                                    </li>
                                    <li class="cr-pagination-current">
                                        <a>5</a>
                                    </li>
                                    <li>
                                        <a>6</a>
                                    </li>
                                    <li class="cr-pagination-skip"></li>
                                    <li>
                                        <a>10</a>
                                    </li>
                                    <li class="cr-pagination-next">
                                        <a aria-label="Próxima">Próxima</a>
                                    </li>
                                </ul>
                            </nav>
                        </GuideSampleCode>
                        <p>Com input:</p>
                        <GuideSampleCode>
                            <nav>
                                <ul class="cr-pagination">
                                    <li class="cr-pagination-previous">
                                        <a aria-label="Anterior">Anterior</a>
                                    </li>
                                    <li class="cr-pagination-input">
                                        <input type="text" value="1" />
                                        <span class="cr-pagination-total">/ 10</span>
                                    </li>
                                    <li class="cr-pagination-next">
                                        <a aria-label="Próxima">Próxima</a>
                                    </li>
                                </ul>
                            </nav>
                        </GuideSampleCode>
                    </GuideSpecification>

                    <GuideSpecification title="Table">
                        <GuideSpecification title="Padrão">
                            <GuideSampleCode>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>A</th>
                                            <th>B</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>1</td>
                                            <td>2</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </GuideSampleCode>
                        </GuideSpecification>

                        <GuideSpecification title="Dotted">
                            <GuideSampleCode>
                                <table class="table-dotted">
                                    <thead>
                                        <tr>
                                            <th>A</th>
                                            <th>B</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>1</td>
                                            <td>2</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </GuideSampleCode>
                        </GuideSpecification>

                        <GuideSpecification title="Striped">
                            <GuideSampleCode>
                                <table class="table-striped">
                                    <thead>
                                        <tr>
                                            <th>A</th>
                                            <th>B</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>1</td>
                                            <td>2</td>
                                        </tr>
                                        <tr>
                                            <td>3</td>
                                            <td>4</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </GuideSampleCode>
                        </GuideSpecification>
                    </GuideSpecification>

                    <GuideSpecification title="Spinner">
                        <GuideSampleCode :framed="true">
                            <div class="cr-spinner"></div>
                        </GuideSampleCode>
                        <GuideSampleCode :framed="true">
                            <div class="cr-spinner-small"></div>
                        </GuideSampleCode>
                    </GuideSpecification>
                </GuideSpecification>

                <GuideSpecification title="Componentes (JS)">
                    <p>
                        Todos os componentes têm funções de inicialização exportadas pelo arquivo
                        "cr-components".
                    </p>
                    <p>É necessário chamá-las após a construção ou mudança no DOM.</p>
                    <GuideSpecification title="Hover Accordion">
                        <ul>
                            <li>
                                Função de inicialização:
                                <code>initHoverAccordions</code>
                            </li>
                            <li>
                                A classe opcional
                                <code>.hover-accordion-keep-state</code> mantém o elemento expandido
                                após o <code>mouseleave</code>.
                            </li>
                            <li>Precisa ter um <code>height</code> definido.</li>
                        </ul>

                        <GuideSampleCode>
                            <div
                                class="hover-accordion hover-accordion-keep-state"
                                style="height: 20rem"
                            >
                                <div class="accordion-item-wrapper keep-state">
                                    <figure class="accordion-item hover-figure">
                                        <img
                                            alt="Foto com detalhe na manga esquerda"
                                            class="lazy"
                                            data-cdn="https://chico-rei.imgix.net"
                                            data-src="https://chico-rei.imgix.net/images/site/2018/produto/new_details/119/1.jpg?auto=compress,format&amp;q=65"
                                        />
                                    </figure>
                                </div>
                                <div class="accordion-item-wrapper">
                                    <figure class="accordion-item hover-figure">
                                        <img
                                            alt="Foto com detalhe da gola U da camiseta"
                                            class="lazy"
                                            data-src="https://chico-rei.imgix.net/images/site/2018/produto/new_details/119/2.jpg?auto=compress,format&amp;q=65"
                                        />
                                    </figure>
                                </div>
                                <div class="accordion-item-wrapper">
                                    <figure class="accordion-item hover-figure">
                                        <img
                                            alt="Foto com detalhe da gola olímpica masculina"
                                            class="lazy"
                                            data-src="https://chico-rei.imgix.net/images/site/2018/produto/new_details/119/3.jpg?auto=compress,format&amp;q=65"
                                        />
                                    </figure>
                                </div>
                                <div class="accordion-item-wrapper">
                                    <figure class="accordion-item hover-figure">
                                        <img
                                            alt="Foto com detalhe de costura no ombro"
                                            class="lazy"
                                            data-src="https://chico-rei.imgix.net/images/site/2018/produto/new_details/119/4.jpg?auto=compress,format&amp;q=65"
                                        />
                                    </figure>
                                </div>
                                <div class="accordion-item-wrapper">
                                    <figure class="accordion-item hover-figure">
                                        <img
                                            alt="Foto com detalhe da camiseta dobrada na embalagem"
                                            class="lazy"
                                            data-src="https://chico-rei.imgix.net/images/site/2018/produto/new_details/119/5.jpg?auto=compress,format&amp;q=65"
                                        />
                                    </figure>
                                </div>
                            </div>
                        </GuideSampleCode>
                    </GuideSpecification>

                    <GuideSpecification title="Modal">
                        <ul>
                            <li>Função de inicialização: <code>initModals</code></li>
                            <li>
                                Qualquer elemento com atributo
                                <code>data-toggle="cr-modal"</code> irá abrir o modal ao ser
                                clicado.
                            </li>
                            <li>
                                O modal a ser aberto é definido pelo atributo
                                <code>data-target="selector-css"</code>.
                            </li>
                            <li>
                                Elementos no modal com o atributo
                                <code>data-dismiss="cr-modal"</code> fecham o modal.
                            </li>
                        </ul>

                        <div class="alert-warning">
                            ! Se o único objetivo do modal for a confirmação de uma ação, utilizar a
                            classe
                            <code>.cr-modal-alert</code>
                        </div>

                        <GuideSampleCode>
                            <a class="btn" data-target="#guide-sample-modal" data-toggle="cr-modal">
                                Abrir o modal
                            </a>
                            <a
                                class="btn"
                                data-target="#guide-sample-modal-alert"
                                data-toggle="cr-modal"
                            >
                                Abrir o alerta
                            </a>

                            <div id="guide-sample-modal" class="cr-modal">
                                <div class="cr-modal-dialog">
                                    <div class="card">
                                        <div class="card-block card-toolbar">
                                            <span class="h5 card-title">Título</span>
                                            <span
                                                aria-label="Fechar"
                                                class="icon-cr icon-close icon-cr-hover"
                                                data-dismiss="cr-modal"
                                            />
                                        </div>
                                        <div class="card-block">Corpo do modal</div>
                                        <div class="card-block text-right">
                                            <button class="btn" data-dismiss="cr-modal">OK</button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div id="guide-sample-modal-alert" class="cr-modal alert">
                                <div class="cr-modal-dialog">
                                    <div class="card">
                                        <div class="card-block card-toolbar">
                                            <span class="h5 card-title">Tem certeza?</span>
                                            <div
                                                class="cr-modal-close"
                                                data-dismiss="cr-modal"
                                            ></div>
                                        </div>
                                        <div class="card-block">
                                            <a class="btn btn-light" data-dismiss="cr-modal">Não</a>
                                            <a class="btn btn-danger" data-dismiss="cr-modal"
                                                >Sim</a
                                            >
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </GuideSampleCode>
                    </GuideSpecification>

                    <GuideSpecification title="Option Group">
                        <ul>
                            <li>
                                Função de inicialização:
                                <code>initOptionGroups</code>
                            </li>
                        </ul>

                        <GuideSampleCode :framed="true">
                            <div class="d-flex flex-spaced-fixed cr-option-group">
                                <div class="cr-option">A</div>
                                <div class="cr-option">B</div>
                                <div class="cr-option">C</div>
                            </div>
                        </GuideSampleCode>
                    </GuideSpecification>

                    <GuideSpecification title="Popover">
                        <ul>
                            <li>
                                Função de inicialização:
                                <code>initPopovers</code>
                            </li>
                            <li>
                                <strong>Dica:</strong> para facilitar a inspeção, basta adicionar a
                                classe <code>.debug</code> ao elemento pai da popover.
                            </li>
                        </ul>

                        <GuideSampleCode :framed="true">
                            <div class="cr-popover-left-top">
                                <p>Passe o mouse aqui.</p>
                                <div class="cr-popover-content">
                                    <span>Texto da popover</span>
                                </div>
                            </div>
                        </GuideSampleCode>
                    </GuideSpecification>

                    <GuideSpecification title="Tooltip">
                        <ul>
                            <li>
                                Função de inicialização:
                                <code>initTooltips</code>
                            </li>
                            <li>
                                Função para habilitar:
                                <code>enableTooltip(elemento)</code>
                            </li>
                            <li>
                                Função para desabilitar:
                                <code>disableTooltip(elemento)</code>
                            </li>
                        </ul>

                        <GuideSampleCode :framed="true">
                            <span
                                data-placement="top"
                                data-toggle="cr-tooltip"
                                title="Texto da tooltip"
                                >Passe o mouse aqui.</span
                            >
                        </GuideSampleCode>
                    </GuideSpecification>
                </GuideSpecification>
            </GuideSpecification>
        </div>
    </div>
</template>

<script>
    import GuideSummary from './GuideSummary';
    import GuideSampleCode from './GuideSampleCode';
    import GuideSpecification from './GuideSpecification';
    import {
        initHoverAccordions,
        initModals,
        initOptionGroups,
        initTooltips,
    } from '../../../common/cr-components';
    import { updateLazyLoad } from '../../../common/common';

    export default {
        name: 'Guide',

        components: { GuideSpecification, GuideSummary, GuideSampleCode },

        data() {
            const styleVariants = ['default', 'primary', 'success', 'warning', 'danger', 'info'];
            const colors = styleVariants.reduce((previous, current) => {
                previous[current] = [];
                return previous;
            }, {});

            return {
                summary: null,
                styleVariants,
                colors,
            };
        },

        mounted() {
            const anchorIndex = window.location.href.indexOf('#');
            const baseUrl =
                anchorIndex >= 0
                    ? window.location.href.slice(0, anchorIndex)
                    : window.location.href;
            this.summary = this.buildSummaryItem(this.$refs.specificationsRoot, baseUrl);
            updateLazyLoad();
            this.initComponents();
            this.buildColors();
        },

        methods: {
            buildSummaryItem(specification, baseUrl) {
                if (
                    !specification.$options ||
                    specification.$options._componentTag !== 'GuideSpecification'
                )
                    return;

                const summaryItem = {
                    name: specification.title,
                    link: baseUrl + '#' + specification.anchor,
                };

                const children = [];
                for (const child of specification.$children) {
                    const item = this.buildSummaryItem(child, baseUrl);
                    if (item) children.push(item);
                }

                if (children.length > 0) {
                    summaryItem.items = children;
                }

                return summaryItem;
            },

            buildColors() {
                for (const variant of this.styleVariants) {
                    for (const el of this.$refs[`${variant}ColorEls`]) {
                        this.colors[variant].push(
                            this.rgbToHex(window.getComputedStyle(el).backgroundColor)
                        );
                    }
                }
            },

            rgbToHex(rgb) {
                const hexDigits = [
                    '0',
                    '1',
                    '2',
                    '3',
                    '4',
                    '5',
                    '6',
                    '7',
                    '8',
                    '9',
                    'a',
                    'b',
                    'c',
                    'd',
                    'e',
                    'f',
                ];

                const hex = (x) =>
                    isNaN(x) ? '00' : hexDigits[(x - (x % 16)) / 16] + hexDigits[x % 16];

                rgb = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
                return '#' + hex(rgb[1]) + hex(rgb[2]) + hex(rgb[3]);
            },

            initComponents() {
                initTooltips();
                initOptionGroups();
                initHoverAccordions();
                initModals();
            },
        },
    };
</script>